import React, { useState, useEffect, useCallback } from "react";
import { View, StyleSheet, Linking, Image, Platform, Dimensions, ScrollView } from "react-native";
import { Spacer, OnlyHeadlineInfoBox, ErrorMessage } from "../../components";
import { Screen, Text, Button } from "../../components";
import { Stack } from "../../components/layout/stack";
import { useNavigation, useFocusEffect } from "@react-navigation/native";
import { useTheme, Checkbox } from "react-native-paper";
import { ThemeType } from "../../theme/theme";
import { translate } from "../../i18n/translate";
import { CheckboxStatusType } from "../../../types";
import { createPayment, createRecoveryPayment } from "../../redux/actions/actionCreators/payment";
import { useDispatch, useSelector } from "react-redux";
import { toHuman } from "../../utils/money";
import { nanoid } from "nanoid/non-secure";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  reviewCertifiedBAPaymentSchema,
  reviewCCPaymentSchema,
  reviewCertifiedCCPaymentSchema,
} from "./schemas";
import HyperLink from "react-native-hyperlink";
import { useConfig } from "../../hooks/useConfig";
import { useComponentSize } from "../../hooks/useComponentSize";
import { PaymentSourceType, InfoBoxType } from "../../enums";
import { setPaymentInReview } from "../../redux/actions/actionCreators";
import { colors } from "../../theme/colors";
import { formatDate, formatDateDo, getDisplayableLocalTime } from "../../utils/date";
import { Inline } from "../../components/layout/inline";
import { HomeParamsList, HomeScreenProps } from "../../navigation/home-stack/home-stack";
import { getUnitNumber, objectToQueryStringParams } from "../../utils";
import { RootState } from "../../redux/reducers";
import { testProps } from "../../utils/testProps";
import * as yup from "yup";
import { UserService } from "../../api/UserService";
import { getErrorMessageId } from "../../utils/errorCodes";
import { BalanceDueExtension } from "../../components/header/balance-due-extension";
import { ProgressIndicator } from "ui-components";
import { useDetermineChannel } from "../../hooks/useDetermineChannel";
const { CREDIT_CARD } = PaymentSourceType;

const windowWidth = Dimensions.get("window").width;
const windowWidthAdjusted = windowWidth > 600 ? 550 : windowWidth - 125;

export const ReviewPayment = ({ route }: HomeScreenProps) => {
  const navigation = useNavigation();
  const { params = {} as HomeParamsList } = route;
  const logger = useSelector((state: RootState) => state.logger);
  let {
    propName,
    propertyUnit,
    paymentDate,
    cardType,
    last4Digits,
    reservationId,
    amounts = {},
    paymentSourceId,
    paymentSourceType,
    paymentScheduleType,
    authorizationDate,
    isCertified = false,
    isDelayedDcash = false,
    isRecoveryPayment = false,
    hasRemainingDCashRecoveryPayments = false,
    blockedReservationId = null,
    balanceDue = 0,
  } = params;

  // These are all bigs
  const { amount, platformFee, total } = amounts;
  const theme = useTheme() as ThemeType;
  const config = useConfig();
  const styles = getStyles(theme);
  const dispatch = useDispatch();
  const formattedPaymentDate = formatDate(paymentDate);
  const formattedAuthorizationDate = formatDate(authorizationDate);
  const formattedAmount = "$" + toHuman(amount);
  const formattedPlatformFee = "$" + toHuman(platformFee);
  const formattedTotal = "$" + toHuman(total);
  const isCC = paymentSourceType === CREDIT_CARD;
  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const userService = new UserService(cognitoUser, dispatch);
  const [viewLayoutSize, setViewLayoutSize] = useComponentSize();

  const [channel] = useDetermineChannel();

  if (params.certifiedPaymentType === "DCASH_BA") {
    cardType = "Account ending in";
  }
  const bankInfoText = `${cardType} ****${last4Digits}`;
  const [isCreatingPayment, setIsCreatingPayment] = useState(false);

  const [cof, setCof] = useState<CheckboxStatusType>("checked");
  const [cancellationRefund, setCancellationRefund] = useState<CheckboxStatusType>(
    isCC ? "unchecked" : "checked"
  );
  const [returnWarning, setReturnWarning] = useState<CheckboxStatusType>("unchecked");
  let defaultValues: any = {
    cof,
    cancellationRefund,
    returnWarning,
  };
  const queryParams: any = {
    scheduleType: paymentScheduleType === "RECURRING" ? "RECURRING" : "ONE_SHOT",
    amount,
    total,
    platformFee,
    reservationId,
    paymentSourceId,
    userNowIso: getDisplayableLocalTime(new Date()).format(),
    paymentDate,
  };

  const pdfUrl = `${
    config.docGenerationService
  }/generateCancellationPolicyPDF?${objectToQueryStringParams(queryParams)}`;

  const paymentSchema = isCertified
    ? isCC
      ? reviewCertifiedCCPaymentSchema
      : reviewCertifiedBAPaymentSchema
    : isCC
    ? reviewCCPaymentSchema
    : yup.object();

  const isContinueDisabled =
    isCreatingPayment ||
    (isCC && cancellationRefund !== "checked") ||
    (isCertified && returnWarning !== "checked");

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(paymentSchema),
    defaultValues,
  });

  const layoutEffect = useCallback(() => {
    dispatch(setPaymentInReview(reservationId));
  }, []);
  useFocusEffect(layoutEffect);

  useEffect(() => {
    register("cof");
    if (isCC) register("cancellationRefund");
    if (isCertified) register("returnWarning");
  }, [register]);

  const onSubmit = async (data: any) => {
    const { cancellationRefund: preCancellationRefund } = data;
    const [finalCof, finalCancellationRefund] = [
      translateCheckToBoolean(cof),
      translateCheckToBoolean(preCancellationRefund),
    ];
    const payload = {
      request_id: nanoid(36),
      payment_source_id: paymentSourceId,
      reservation_id: reservationId,
      schedule_date: paymentDate,
      schedule_type: paymentScheduleType,
      amount: amounts.amount,
      // TODO: may need to change when dcash is implemented
      type: paymentScheduleType === "RECURRING" ? "RECURRING" : "ONE_SHOT",
      allow_cof: finalCof,
      cancellation_refund: finalCancellationRefund,
      is_certified: isCertified,
      is_delayed_dcash: isDelayedDcash,
    };

    if (isCertified) {
      payload.is_certified = isCertified;
    }

    try {
      await logger.log({ payload, reservationId });
      setIsCreatingPayment(true);
      let result;
      if (isRecoveryPayment) {
        payload.blocked_reservation_id = blockedReservationId;
        result = await dispatch(createRecoveryPayment(payload, reservationId));
      } else {
        result = await dispatch(createPayment(payload, reservationId));
      }

      const { payment_schedule_id } = result.payload.data;
      userService.sendFootprints({
        payment_schedule_id,
        username: userInfo.username,
        interaction_type: "PAYMENT_SCHEDULE",
        channel,
      });

      let messageBody =
        paymentScheduleType === "RECURRING"
          ? translate("homeStack.autoPayScheduleSuccess", {
              formattedDate: formatDateDo(paymentDate),
            })
          : isCertified
          ? translate("homeStack.certifiedManagerApproval")
          : translate("homeStack.paymentPendingManagerApproval");

      let headline = isCertified
        ? translate("homeStack.certifiedPaymentSubmitted")
        : translate("homeStack.success");

      if (isRecoveryPayment) {
        messageBody = hasRemainingDCashRecoveryPayments
          ? translate("dcashRecoveryStack.paymentSuccessRemainingBalance")
          : translate("dcashRecoveryStack.paymentSuccessNoBalance");
        headline = translate("homeStack.success");
      }

      setIsCreatingPayment(false);

      logger.infoObject({
        reservationId: reservationId,
        paymentSourceId: paymentSourceId,
        paymentScheduleType: paymentScheduleType,
        message: "Create payment succeeded",
        paymentSourceType,
      });

      navigation.navigate("PaymentSummary", {
        ...params,
        bankInfoText,
        createPaymentResult: {
          type: "SUCCESS",
          infoBoxType: InfoBoxType.GREEN,
          headline: headline,
          messageBody,
        },
      });
    } catch (e) {
      setIsCreatingPayment(false);
      const errorData = e?.error?.response?.data;
      await logger.error(errorData ? errorData : e);
      const serverErrorCode = e?.error?.response?.data?.code;
      const error = {
        code: serverErrorCode,
      };
      const messageId = getErrorMessageId(error);
      const isVantivError = e?.error?.response?.data?.failedZeroAuth;
      const duplicatePaymentError = e?.error?.response?.data?.duplicatePaymentError;
      let headline = isVantivError
        ? translate("homeStack.paymentUnsuccessful")
        : isCertified
        ? translate("homeStack.certifiedPaymentUnsuccessful")
        : translate("homeStack.paymentUnsuccessful");
      let messageBody = isVantivError
        ? messageId
          ? translate(messageId)
          : translate("errors.customGeneralPayment")
        : duplicatePaymentError
        ? translate("homeStack.duplicatePaymentBody")
        : translate("homeStack.useDifferentPaymentMethod");
      if (serverErrorCode === "DCRF02") {
        headline = translate(`$errors.${serverErrorCode}.title`);
        messageBody = translate(`$errors.${serverErrorCode}.message`);
      }
      // pull error
      await logger.error({
        exception: errorData ? errorData : e,
        reservationId: reservationId,
        paymentSourceId: paymentSourceId,
        paymentScheduleType: paymentScheduleType,
        message: `Create payment error`,
        paymentSourceType,
      });
      navigation.navigate("PaymentSummary", {
        ...params,
        createPaymentResult: {
          type: "FAILURE",
          infoBoxType: InfoBoxType.RED,
          headline,
          messageBody,
        },
      });
    }
  };

  const renderBankInfo = () => {
    return isCertified ? (
      <Inline space="smaller">
        <Image
          style={styles.iconSize}
          source={require("../../../assets/certified_checkmark.png")}
        />
        <Text>{bankInfoText}</Text>
      </Inline>
    ) : (
      bankInfoText
    );
  };

  return (
    <Screen
      style={styles.container}
      preset="scroll"
      backgroundColor={theme.colors.background}
      dontUseAutoPadding
      unsafe={true}>
      <Stack space="large" style={styles.mainStack}>
        <ScrollView scrollEnabled={true} style={styles.scrollView}>
          <View>
            <BalanceDueExtension balance={balanceDue} />
            <View style={{ marginTop: 20 }}>
              <ProgressIndicator
                stepCount={2}
                currentPosition={1}
                labels={[
                  translate("homeStack.step1Payment") as string,
                  translate("homeStack.step2Payment") as string,
                ]}
                {...testProps("review-payment@progress-indicator")}
              />
            </View>
          </View>
          <View style={{ marginTop: theme.spacing.small }} onLayout={setViewLayoutSize}>
            <Stack space="smaller">
              <Text style={styles.sectionTitle} tx="homeStack.paymentSummary"></Text>
            </Stack>
            <View style={styles.sectionContent}>
              {paymentScheduleType === "RECURRING" ? (
                <Stack space="smaller">
                  <Row styles={styles} title="Unit" info={getUnitNumber(propertyUnit)} />
                  <Row styles={styles} title="Total" info="Balance due + Platform Fee" />
                  <Row styles={styles} title="Method" info={bankInfoText} />
                  <Row
                    styles={styles}
                    title="Date"
                    info={`Monthly starting ${formattedPaymentDate}`}
                  />
                </Stack>
              ) : (
                <Stack space="smaller">
                  {propName && <Row styles={styles} title="Property" info={propName} />}
                  <Row styles={styles} title="Unit" info={getUnitNumber(propertyUnit)} />
                  {authorizationDate && (
                    <Row
                      styles={styles}
                      title="Authorization Date"
                      info={formattedAuthorizationDate}
                    />
                  )}
                  <Row styles={styles} title="Payment Date" info={formattedPaymentDate} />
                  <Row styles={styles} title="Method" info={renderBankInfo()} />
                  <Row styles={styles} title="Amount" info={formattedAmount} />
                  <Row styles={styles} title="Platform Fee" info={formattedPlatformFee} />
                  <Row styles={styles} title="Total" info={formattedTotal} isBold />
                </Stack>
              )}
              <Spacer />
              <Stack>
                {isCC && (
                  <Stack space="tiny">
                    <View style={styles.checkboxRow} accessible={true}>
                      <Checkbox.Android
                        status={cancellationRefund}
                        color={theme.colors.darkBg}
                        onPress={async () => {
                          const nextState = getNextLabelState(cancellationRefund);
                          await logger.log({ nextState });
                          setCancellationRefund(nextState);
                          setValue("cancellationRefund", nextState);
                        }}
                        {...testProps("cancellation-refund")}
                      />
                      <HyperLink
                        linkStyle={{ color: theme.colors.primary }}
                        onPress={() => Linking.openURL(pdfUrl)}
                        linkText={(url) =>
                          url === pdfUrl ? "cancellation and refund policy" : url
                        }>
                        <Text style={styles.checkboxLabel}>
                          I accept Domuso's{" "}
                          <Text
                            style={{ color: colors.link }}
                            onPress={() => Linking.openURL(pdfUrl)}>
                            cancellation and refund policy
                          </Text>
                          . You will be alerted to any policy changes via email.
                        </Text>
                      </HyperLink>
                    </View>
                    <ErrorMessage errors={errors} formName="cancellationRefund" />
                  </Stack>
                )}
                {isCertified && (
                  <Stack space="tiny">
                    <View style={styles.checkboxRow} accessible={true}>
                      <Checkbox.Android
                        status={returnWarning}
                        color={theme.colors.darkBg}
                        onPress={async () => {
                          const nextState = getNextLabelState(returnWarning);
                          await logger.log({ nextState });
                          setReturnWarning(nextState);
                          setValue("returnWarning", nextState);
                        }}
                        {...testProps("return-warning")}
                      />
                      <Text style={styles.checkboxLabel} tx="homeStack.returnWarningMessage" />
                    </View>
                    <ErrorMessage errors={errors} formName="returnWarning" />
                  </Stack>
                )}
              </Stack>
              <Spacer size="small" />
              <Stack>
                {paymentScheduleType === "RECURRING" || isRecoveryPayment ? null : (
                  <OnlyHeadlineInfoBox
                    infoBoxType={InfoBoxType.ORANGE}
                    headlineWidth="90%"
                    headline={translate("homeStack.bySubmittingPaymentNotice")}
                    borderRadius={5}
                  />
                )}
                <View style={styles.buttonContainer}>
                  <Button
                    mode="contained"
                    disabled={isContinueDisabled}
                    loading={isCreatingPayment}
                    tx="homeStack.submitPayment"
                    onPress={handleSubmit(onSubmit)}
                    {...testProps("submit-payment-button")}
                  />
                </View>
              </Stack>
            </View>
          </View>
        </ScrollView>
      </Stack>
    </Screen>
  );
};

const getNextLabelState = (label: string) => {
  switch (label) {
    case "indeterminate":
      return "checked";
    case "checked":
      return "unchecked";
    case "unchecked":
      return "checked";
    default:
      return "checked";
  }
};

const translateCheckToBoolean = (label: string) => {
  switch (label) {
    case "indeterminate":
      return false;
    case "checked":
      return true;
    case "unchecked":
      return false;
    default:
      return false;
  }
};

interface IRowProps extends Record<string, any> {
  styles: any;
  title: string;
  info: any;
  isBold?: boolean;
}

const Row = ({ styles, title, info, isBold = false }: IRowProps) => {
  const content = typeof info === "string" ? <Text>{info}</Text> : info;
  return (
    <View style={styles.confirmPaymentRow}>
      <Text style={styles.rowLabel}>{title}</Text>
      {content}
    </View>
  );
};

const getStyles = (theme: ThemeType) => {
  const { colors, spacing, fontSize } = theme;
  const windowInfo = Dimensions.get("window");

  return StyleSheet.create({
    container: {
      paddingBottom: spacing.large,
      flexDirection: "row",
      justifyContent: "center",
      ...Platform.select({
        native: {
          backgroundColor: colors.background,
        },
        default: {
          backgroundColor: colors.lightGrey,
        },
      }),
    },
    scrollView: {
      ...Platform.select({
        //"native" will target both iOS and Android devices.
        native: {
          //Do not specify height on native, it gets automatically handled.
          paddingTop: 0,
          paddingLeft: 0,
          paddingRight: 0,
          paddingBottom: 10,
        },
        //"default" will affect web and anything that is NOT iOS or Android device
        default: {
          //height is required for web since scrollview container gets rendered differently from mobile.
          //The operation is to account for header component height.
          height: windowInfo ? windowInfo.height - 200 : 600,
          paddingTop: 25,
          paddingLeft: 25,
          paddingRight: 25,
          paddingBottom: 25,
        },
      }),
    },
    mainStack: {
      display: "flex",
      flex: 1,
      maxWidth: 804,
    },
    iconSize: {
      height: theme.iconSize.small,
      width: theme.iconSize.small,
      resizeMode: "contain",
    },
    sectionTitle: {
      paddingTop: 15,
      paddingLeft: 15,
      paddingBottom: 8,
      fontWeight: "600",
    },
    sectionContent: {
      paddingHorizontal: spacing.medium,
    },
    reviewAndPay: {
      fontSize: fontSize.large,
      fontWeight: "600",
    },
    rowLabel: {},
    checkboxLabel: {
      textAlign: "left",
      fontSize: fontSize.medium,
      ...Platform.select({
        native: {
          maxWidth: 280,
        },
        default: {
          maxWidth: windowWidthAdjusted,
        },
      }),
    },
    confirmPaymentRow: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
    checkboxRow: {
      flexDirection: "row",
      justifyContent: "center",
    },
    buttonContainer: {
      ...Platform.select({
        default: {
          width: windowWidth <= 375 ? "100%" : 325,
          marginLeft: "auto",
          marginRight: "auto",
        },
      }),
    },
  });
};
